import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { trace, context, SpanStatusCode } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';

function detectEnvironment(): string {
  const hostname = window.location.hostname;
  if (hostname.includes('localhost') || hostname.includes('dev')) {
    return 'development';
  } else if (hostname.includes('staging')) {
    return 'staging';
  } 
  return 'production';
}

async function loadConfigFromFile(): Promise<any> {
  try {
    const response = await fetch('/telemetry-config.json');
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const config = await response.json();
    const environment = detectEnvironment();
    const selectedConfig = config[environment] || config.development;
    
    return selectedConfig;
  } catch (error) {
    console.error('[telemetry] Failed to load config:', error);
    return {
      enabled: false,
      serviceName: 'ped-backstage-service-fallback',
      environment: 'unknown',
      signozEndpoint: 'https://ingest.us.signoz.cloud:443',
      apiKey: '',
      headers: {}
    };
  }
}

export async function initializeTelemetry(): Promise<void> {
  try {
    const TELEMETRY_CONFIG = await loadConfigFromFile();

    if (!TELEMETRY_CONFIG.enabled) {
      console.log('[telemetry] Telemetry is disabled');
      return;
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...TELEMETRY_CONFIG.headers
    };

    if (TELEMETRY_CONFIG.apiKey) {
      headers['signoz-access-token'] = TELEMETRY_CONFIG.apiKey;
    }
    
    const traceExporter = new OTLPTraceExporter({
      url: `${TELEMETRY_CONFIG.signozEndpoint}/v1/traces`,
      headers: headers,
      timeoutMillis: 10000,
    });
    const spanProcessor = new BatchSpanProcessor(traceExporter);
    
    const resource = resourceFromAttributes({
      [SemanticResourceAttributes.SERVICE_NAME]: TELEMETRY_CONFIG.serviceName,
      [SemanticResourceAttributes.SERVICE_VERSION]: '1.0.0',
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: TELEMETRY_CONFIG.environment,
    });
    
    const provider = new WebTracerProvider({
      resource: resource,
      spanProcessors: [spanProcessor],
    });

    provider.register();
    
    registerInstrumentations({
      instrumentations: [
        new UserInteractionInstrumentation({
          eventNames: ['click', 'dblclick', 'submit'],
          shouldPreventSpanCreation: (_eventType, element, _span) => {
            const className = element.className?.toString() || '';
            
            // Skip tracking for certain UI elements to reduce noise
            if (className.includes('MuiButtonBase') && !className.includes('important-button')) {
              return true;
            }
            
            return false;
          },
        }),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [/.*/],
          clearTimingResources: true,
          ignoreUrls: [/health/, /metrics/, /ping/],
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: [/.*/],
          clearTimingResources: true,
          ignoreUrls: [/health/, /metrics/, /ping/],
        }),
        new DocumentLoadInstrumentation(),
      ],
    });

    window.OTEL_FRONTEND_STATUS = 'INITIALIZED';
    window.OTEL_SERVICE_NAME = TELEMETRY_CONFIG.serviceName;
    window.OTEL_ENVIRONMENT = TELEMETRY_CONFIG.environment;
    window.OTelUtils = { getTracer, withSpan };
    
  } catch (error: any) {
    window.OTEL_FRONTEND_STATUS = 'FAILED';
    window.OTEL_ERROR = error.message;
    console.error('[telemetry] Failed to initialize:', error);
  }
}

export function getTracer(name = 'frontend-tracer') {
  return trace.getTracer(name);
}

export async function withSpan<T>(name: string, fn: () => Promise<T> | T): Promise<T> {
  const tracer = getTracer();
  const span = tracer.startSpan(name);
  return context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const result = await fn();
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error: any) {
      span.setStatus({ code: SpanStatusCode.ERROR, message: error?.message ?? 'Unknown error' });
      span.recordException(error);
      throw error;
    } finally {
      span.end();
    }
  });
}

initializeTelemetry().catch(error => {
  console.error('[telemetry] Failed to initialize telemetry:', error);
});
